import "./stylesheets/fonts.css";
import "./stylesheets/reset.css";
import "./stylesheets/theme.css";

import type { SigninRedirectArgs, UserProfile } from "oidc-client-ts";
import { User } from "oidc-client-ts";

import { userManager } from "./user-manager";

const { VITE_AUTH_ADMIN_BASE_URL: AUTH_ADMIN_BASE_URL = "" } = import.meta.env;

const isLogoutRoute = window.location.pathname === "/logout";

const initiateSignIn = async (
  arguments_?: SigninRedirectArgs,
): Promise<void> => {
  try {
    await userManager.signinRedirect(arguments_);
  } catch (error) {
    console.error("Error initiating sign-in:", error);
  }
};

async function parseAuthResponse(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type") ?? "";
  const responseText = await response.text();

  if (!response.ok) {
    throw new Error(
      `Admin auth failed (${response.status}): ${
        responseText || "No response body"
      }`,
    );
  }

  if (!contentType.includes("application/json")) {
    throw new Error(
      `Admin auth returned unexpected content type: ${
        contentType || "unknown"
      }`,
    );
  }

  try {
    return JSON.parse(responseText) as unknown;
  } catch {
    throw new Error(
      `Admin auth returned invalid JSON: ${responseText.slice(0, 200)}`,
    );
  }
}

const handleCallback = async (): Promise<void> => {
  const urlParameters = new URLSearchParams(window.location.search);
  const code = urlParameters.get("code");

  if (code) {
    try {
      const response = await fetch(AUTH_ADMIN_BASE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      const responseData = (await parseAuthResponse(response)) as {
        access_token: string;
        expires_at: number;
        id_token: string;
        profile: UserProfile;
        scope: string;
        token_type: string;
      };

      const userToStore = new User({
        id_token: responseData.id_token,
        access_token: responseData.access_token,
        token_type: responseData.token_type,
        scope: responseData.scope,
        profile: responseData.profile,
        expires_at: responseData.expires_at,
      });

      await userManager.storeUser(userToStore);
      // Replace callback URL with a clean app route after successful auth.
      // eslint-disable-next-line no-restricted-globals
      history.replaceState(undefined, "", "/");
      renderApp();
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }
};

const renderApp = () => {
  void import("./init");
};

(async () => {
  const user = await userManager.getUser();

  if (isLogoutRoute) {
    await userManager.removeUser();
    await userManager.clearStaleState();
    await initiateSignIn({
      extraQueryParams: {
        prompt: "select_account",
      },
    });
    return;
  }

  if (!user && window.location.pathname !== "/auth/callback") {
    await initiateSignIn();
  } else if (window.location.pathname === "/auth/callback") {
    await handleCallback();
  } else {
    renderApp();
  }
})();
